import { isPaid, monthlyEquivalent, type CardMeta, type VulpariNode } from "@vulpari/core";
import { Handle, Position, type NodeProps } from "@xyflow/react";

import { formatMoney, formatMonthLabel } from "../lib/format.js";
import { useStore } from "../store.js";
import { Icon, type IconName } from "./Icon.js";

/** Node types that can be ticked off as paid for the month. */
const PAYABLE = new Set<VulpariNode["type"]>(["bill", "card", "oneoff", "pot", "external"]);

const NODE_ICON: Record<VulpariNode["type"], IconName> = {
  account: "bank",
  card: "credit-card",
  income: "money-wavy",
  bill: "receipt",
  oneoff: "calendar-dot",
  pot: "piggy-bank",
  external: "storefront",
};

const NODE_ICON_COLOUR: Record<VulpariNode["type"], string> = {
  account: "text-neutral",
  card: "text-warning",
  income: "text-positive",
  bill: "text-neutral",
  oneoff: "text-accent",
  pot: "text-positive",
  external: "text-ink-muted",
};

const CARD_KIND_LABEL: Record<NonNullable<CardMeta["kind"]>, string> = {
  credit_card: "Credit card",
  loan: "Loan",
  payment_plan: "Payment plan",
};

/** A progress bar for a card-like node: utilisation for a credit card, or how
 * much has been paid off for a loan or payment plan. `balance` is the amount
 * owed for the viewed month (after that month's payment). */
function CardBar({ meta, balance }: { meta: CardMeta; balance: number }) {
  const kind = meta.kind ?? "credit_card";

  // Without a reference amount there is nothing to measure progress against, so
  // prompt for it rather than hiding the bar silently.
  if (meta.creditLimit <= 0) {
    return (
      <p className="mt-2 text-xs text-ink-muted">
        {kind === "credit_card"
          ? "Set a credit limit to see utilisation."
          : "Set the original amount to see progress."}
      </p>
    );
  }

  const usedRatio = balance / meta.creditLimit;
  const isCredit = kind === "credit_card";
  const fill = Math.max(0, Math.min(1, isCredit ? usedRatio : 1 - usedRatio));
  const label = isCredit
    ? `${Math.round(usedRatio * 100)}% used`
    : `${Math.round((1 - usedRatio) * 100)}% paid off`;
  const colour =
    isCredit && usedRatio > 0.7
      ? "var(--colour-warning)"
      : isCredit
        ? "var(--colour-accent)"
        : "var(--colour-positive)";

  return (
    <div className="mt-2">
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-canvas">
        <div
          className="h-full rounded-full"
          style={{ width: `${fill * 100}%`, backgroundColor: colour }}
        />
      </div>
      <p className="mt-1 text-xs text-ink-muted">{label}</p>
    </div>
  );
}

export interface MoneyNodeData {
  node: VulpariNode;
  computedBalance?: number;
  /** Total funded into this node this computation (used by bills accounts). */
  aggregate?: number;
  /** Highlighted as a grouping target while another node is dragged over it. */
  highlight?: boolean;
  [key: string]: unknown;
}

const TYPE_LABEL: Record<VulpariNode["type"], string> = {
  account: "Account",
  card: "Card",
  income: "Income",
  bill: "Bill",
  oneoff: "One-off",
  pot: "Pot",
  external: "External",
};

// A calm border accent per node type, using theme tokens only.
const TYPE_BORDER: Record<VulpariNode["type"], string> = {
  account: "border-l-neutral",
  card: "border-l-warning",
  income: "border-l-positive",
  bill: "border-l-neutral",
  oneoff: "border-l-accent",
  pot: "border-l-positive",
  external: "border-l-ink-muted",
};

/** The headline figure a node shows, if any. */
function primaryFigure(node: VulpariNode): { label: string; amount: number } | null {
  switch (node.type) {
    case "account":
      return { label: "Balance", amount: node.meta.balance };
    case "card":
      return { label: "Owed", amount: node.meta.balance };
    case "income":
      return { label: "Income", amount: node.meta.amount };
    case "bill":
      return { label: "Bill", amount: node.meta.amount };
    case "oneoff":
      return { label: "One-off", amount: node.meta.amount };
    case "pot":
      return { label: "Saved", amount: node.meta.balance };
    case "external":
      return null;
  }
}

export function MoneyNode({ data, selected }: NodeProps) {
  const { node, computedBalance, aggregate, highlight } = data as MoneyNodeData;

  const month = useStore((state) => state.context.month);
  const togglePaid = useStore((state) => state.togglePaid);
  const payable = PAYABLE.has(node.type);
  const paid = payable && isPaid(node, month);

  // A one-off belongs to a single month, shown here on the node. Outside that
  // month the canvas hides it entirely, so it is only ever rendered in-month.
  const oneoffDueMonth = node.type === "oneoff" ? node.meta.dueDate.slice(0, 7) : null;

  // A bills account's headline is the total of the bills it covers.
  const isBillsAccount = node.type === "account" && node.meta.role === "bills";
  const isAccount = node.type === "account" && !isBillsAccount;
  const figure = primaryFigure(node);
  // Account, card and pot all show their balance at the end of the viewed month
  // (after that month's flows), so they read consistently.
  const endOfMonthBalance = computedBalance ?? figure?.amount ?? 0;
  const isValueHolding = isAccount || node.type === "card" || node.type === "pot";

  // What next month's interest will be on a carried card balance (APR is annual,
  // in basis points; a twelfth applies each month). Zero when cleared or 0%.
  const cardInterest =
    node.type === "card" && node.meta.aprBasisPoints > 0 && endOfMonthBalance > 0
      ? Math.round((endOfMonthBalance * node.meta.aprBasisPoints) / 120000)
      : 0;

  return (
    <div
      className={`w-full rounded-xl border border-l-4 border-node-border ${TYPE_BORDER[node.type]} bg-node-bg px-4 py-3 shadow-lg ${
        highlight ? "ring-2 ring-positive" : selected ? "ring-2 ring-accent" : ""
      } ${paid ? "opacity-60" : ""}`}
    >
      <Handle type="target" position={Position.Left} className="!bg-neutral" />

      <div className="flex items-center gap-2">
        <Icon name={NODE_ICON[node.type]} size={18} className={NODE_ICON_COLOUR[node.type]} />
        <div className="min-w-0 flex-1">
          <p className="text-xs uppercase tracking-wide text-ink-muted">
            {node.type === "card"
              ? CARD_KIND_LABEL[node.meta.kind ?? "credit_card"]
              : TYPE_LABEL[node.type]}
          </p>
          <p className="text-sm font-semibold text-ink">{node.name}</p>
        </div>
        {payable ? (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              togglePaid(node.id);
            }}
            onPointerDown={(event) => event.stopPropagation()}
            aria-pressed={paid}
            title={paid ? "Paid this month" : "Mark as paid"}
            className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[11px] leading-none transition-colors ${
              paid
                ? "border-positive bg-positive text-canvas"
                : "border-node-border text-transparent hover:border-positive hover:text-positive"
            }`}
          >
            ✓
          </button>
        ) : null}
      </div>

      {isBillsAccount ? (
        <>
          <p className="figure mt-1 text-lg text-ink">
            {formatMoney(aggregate ?? 0, node.currency)}
          </p>
          <p className="mt-0.5 text-xs text-ink-muted">Total of bills covered</p>
        </>
      ) : isValueHolding ? (
        <p className={`figure mt-1 text-lg text-ink ${paid ? "line-through" : ""}`}>
          {formatMoney(endOfMonthBalance, node.currency)}
        </p>
      ) : figure ? (
        <p className={`figure mt-1 text-lg text-ink ${paid ? "line-through" : ""}`}>
          {formatMoney(figure.amount, node.currency)}
        </p>
      ) : null}

      {node.type === "oneoff" && oneoffDueMonth ? (
        <p className="mt-0.5 text-xs text-ink-muted">Due {formatMonthLabel(oneoffDueMonth)}</p>
      ) : null}

      {node.type === "card" ? <CardBar meta={node.meta} balance={endOfMonthBalance} /> : null}

      {cardInterest > 0 ? (
        <p className="figure mt-0.5 text-xs text-ink-muted">
          +{formatMoney(cardInterest, node.currency)} interest/mo
        </p>
      ) : null}

      {node.type === "bill" && node.meta.frequency !== "monthly" ? (
        <p className="figure mt-0.5 text-xs text-ink-muted">
          {node.meta.frequency}, approx{" "}
          {formatMoney(monthlyEquivalent(node.meta.amount, node.meta.frequency), node.currency)} per
          month
        </p>
      ) : null}

      <Handle type="source" position={Position.Right} className="!bg-accent" />
    </div>
  );
}
